import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import api from './api';

// Generate a session ID if one doesn't exist
const getSessionId = () => {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2);
    localStorage.setItem('analytics_session_id', sessionId);
    
    // Set session expiry (30 minutes from now)
    const expiry = Date.now() + (30 * 60 * 1000);
    localStorage.setItem('analytics_session_expiry', expiry.toString());
  } else {
    // Check if session has expired
    const expiry = parseInt(localStorage.getItem('analytics_session_expiry') || '0');
    if (Date.now() > expiry) {
      // Create a new session
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2);
      localStorage.setItem('analytics_session_id', sessionId);
      
      // Set new expiry
      const newExpiry = Date.now() + (30 * 60 * 1000);
      localStorage.setItem('analytics_session_expiry', newExpiry.toString());
    } else {
      // Extend existing session
      const newExpiry = Date.now() + (30 * 60 * 1000);
      localStorage.setItem('analytics_session_expiry', newExpiry.toString());
    }
  }
  
  return sessionId;
};

const AnalyticsTracker = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('token');
  
  // Track page views whenever location changes
  useEffect(() => {
    // Debounce pageview tracking
    const lastPageView = localStorage.getItem('analytics_last_pageview');
    const lastPath = localStorage.getItem('analytics_last_path');
    const now = Date.now();
    
    // Don't track if:
    // 1. We're in development mode with analytics disabled
    // 2. This is the same path viewed within the last 2 seconds (debounce)
    if (
      (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DISABLE_ANALYTICS === 'true') ||
      (lastPageView && lastPath === location.pathname && now - parseInt(lastPageView) < 2000)
    ) {
      return;
    }
    
    // Track the pageview
    const trackPageView = async () => {
      try {
        // Update last pageview time and path
        localStorage.setItem('analytics_last_pageview', now.toString());
        localStorage.setItem('analytics_last_path', location.pathname);
        
        // Get device information
        const isMobile = window.innerWidth <= 768;
        const deviceType = isMobile ? 'mobile' : 'desktop';
        const language = localStorage.getItem('language') || 'nl';
        const sessionId = getSessionId();
        
        // Record page view
        await api.post('/api/analytics/pageview', {
          path: location.pathname,
          isAuthenticated,
          deviceType,
          language,
          timestamp: new Date().toISOString(),
          referrer: document.referrer || null,
          sessionId
        });
      } catch (error) {
        // Silent fail - don't disrupt user experience if analytics fails
        console.error('Analytics error:', error);
      }
    };
    
    trackPageView();
  }, [location.pathname, isAuthenticated]);
  
  return children;
};

// Export a function to track custom events
export const trackEvent = async (eventName, eventData = {}) => {
  // Skip tracking in development if disabled
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DISABLE_ANALYTICS === 'true') {
    return;
  }
  
  try {
    const isMobile = window.innerWidth <= 768;
    const deviceType = isMobile ? 'mobile' : 'desktop';
    const language = localStorage.getItem('language') || 'nl';
    const isAuthenticated = !!localStorage.getItem('token');
    const sessionId = getSessionId();
    
    await api.post('/api/analytics/event', {
      eventName,
      eventData,
      isAuthenticated,
      deviceType,
      language,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      sessionId
    });
  } catch (error) {
    console.error('Analytics event error:', error);
  }
};

export default AnalyticsTracker;